import {Injectable, isDevMode} from '@angular/core';
import {AngularFirestore} from "@angular/fire/compat/firestore";
import {EducationObservation} from "./education-observation.model";
import firebase from "firebase/compat/app";
import {combineLatest, map, Observable, of} from "rxjs";
import {Timestamp} from "firebase/firestore";
import {forEach} from "lodash";
import {environment} from "../../../../../environments/environment";
import {EmployeeService} from "../../../../services/employee.service";
import {
  buildDirectoryLevels,
  canView,
  getCurrentUserLevel,
  ObservationLevel,
  observedLevelOf,
  teacherTypeFromLevel
} from "./education-observation-access";
@Injectable({
  providedIn: 'root'
})
export class EducationObservationService {
  env = isDevMode() ? environment.testMode : environment.productionMode;
  private collection = this.firestore.collection<EducationObservation>(this.env.educationObservation);

  constructor(private firestore: AngularFirestore,
              private employeeService: EmployeeService) {
  }

  create(data: EducationObservation): Promise<any> {
    const payload: EducationObservation = {
      ...data,
      createdAt: firebase.firestore.FieldValue.serverTimestamp() as any,
      updatedAt: firebase.firestore.FieldValue.serverTimestamp() as any,
    };
    return this.collection.add(payload);
  }

  getDataById(id: string): Observable<EducationObservation | undefined> {
    return this.collection.doc<EducationObservation>(id).valueChanges({idField: 'id'});
  }

  getAll2(): Observable<EducationObservation[]> {
    return this.collection.valueChanges({idField: 'id'});
  }
  getAll(): Observable<EducationObservation[]> {
    return this.firestore.collection<EducationObservation>(
      this.env.educationObservation, ref => ref.orderBy('sessionDate', 'desc')).valueChanges({ idField: 'id' });
  }

  /** userName -> level for everyone in the user directory. */
  private directoryLevels$(): Observable<Map<string, ObservationLevel>> {
    return this.employeeService.getAllUsers().pipe(map(users => buildDirectoryLevels(users)));
  }

  /**
   * Observations the signed-in user is allowed to see: only those written about
   * someone below them in the hierarchy (see education-observation-access.ts).
   */
  getVisibleObservations(): Observable<EducationObservation[]> {
    const viewerLevel = getCurrentUserLevel();
    if (viewerLevel === ObservationLevel.NONE) {
      return of([]);
    }

    return combineLatest([this.getAll(), this.directoryLevels$()]).pipe(
      map(([observations, directoryLevels]) => observations
        .map(observation => ({observation, level: observedLevelOf(observation, directoryLevels)}))
        .filter(({level}) => canView(viewerLevel, level))
        // Normalise teacherType against the directory so the list filter and the
        // table agree with the level the record was actually filtered on.
        .map(({observation, level}) => ({...observation, teacherType: teacherTypeFromLevel(level)})))
    );
  }

  /** Whether the signed-in user may open a single observation. */
  canViewObservation(observation: EducationObservation | null | undefined): Observable<boolean> {
    const viewerLevel = getCurrentUserLevel();
    return this.directoryLevels$().pipe(
      map(directoryLevels =>
        !!observation && canView(viewerLevel, observedLevelOf(observation, directoryLevels)))
    );
  }



  update(id: string, data: Partial<EducationObservation>): Promise<void> {
    const payload = {
      ...data,
      updatedAt: firebase.firestore.FieldValue.serverTimestamp()
    };
    return this.collection.doc(id).update(payload);
  }

  deleteRow(id: string): Promise<void> {
    return this.collection.doc(id).delete();
  }

  searchByCentreAndTeacher(centreCode: string, teacher?: string | null) {
    let query: firebase.firestore.Query<any> =
      this.firestore.firestore.collection(this.env.educationObservation)
        .orderBy('sessionDate', 'desc');

    // Filter by centre
    if (centreCode && centreCode !== 'All') {
      query = query.where('centreCode', '==', centreCode);
    }

    // Filter by teacher
    if (teacher && teacher !== 'All') {
      query = query.where('teacherName', '==', teacher);
    }

    return query.get();
  }

  bulkChangeObservationData() {
    this.firestore.firestore.collection(this.env.educationObservation).get().then(snapshot => {
      // 1. Initialize a Write Batch
      const batch = this.firestore.firestore.batch();

      snapshot.docs.forEach(doc => {
        const it = doc.data() as any;
        const docRef = doc.ref;

        // 2. Prepare the child object
        // Logic check: only proceed if the old fields exist
        if (it.childName || it.childCode) {
          const childObj = {
            childName: it.childName || '',
            childCode: it.childCode || ''
          };

          // 3. Add the update to the batch
          // Using arrayUnion ensures 'childObj' is added to the 'children' array
          batch.update(docRef, {
            children: firebase.firestore.FieldValue.arrayUnion(childObj)
          });
        }
      });

      // 4. Commit all updates at once
      return batch.commit();
    })
      .then(() => {
        console.log('Bulk update completed successfully!');
      })
      .catch(error => {
        console.error("Error during bulk update:", error);
      });
  }
}
